/* Only adapt this navigation. Native chat, consent, age and cart are read-only. */
if (!customElements.get('gjl-mobile-nav')) {
  customElements.define('gjl-mobile-nav', class extends HTMLElement {
    connectedCallback() {
      this.lifetime = new AbortController();
      this.frame = 0;
      this.roots = new WeakSet();
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
      };
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(document.body, {subtree: true, childList: true, attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'open', 'aria-hidden', 'aria-modal', 'inert', 'data-gjl-chat-stowed']});
      this.observer.observe(document.documentElement, {attributes: true, attributeFilter: ['class', 'style', 'data-gyj-age']});
      this.resize = new ResizeObserver(this.schedule);
      this.resize.observe(document.documentElement);
      const options = {passive: true, signal: this.lifetime.signal};
      window.addEventListener('resize', this.schedule, options);
      window.addEventListener('scroll', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      window.visualViewport?.addEventListener('scroll', this.schedule, options);
      // Upgrade may attach the native launcher's shadow root asynchronously.
      customElements.whenDefined('shopify-chat').then(() => {
        if (this.isConnected && !this.lifetime.signal.aborted) this.schedule();
      });
      this.schedule();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.lifetime?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
    }

    visible(element) {
      if (!element || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0;
    }

    launcher(root) {
      if (!root) return null;
      if (!this.roots.has(root)) {
        this.observer.observe(root, {subtree: true, childList: true, attributes: true});
        this.roots.add(root);
      }
      for (const button of root.querySelectorAll('[part~="activator"]')) {
        if (this.visible(button)) return button;
      }
      for (const child of root.querySelectorAll('*')) {
        if (child.shadowRoot) {
          const button = this.launcher(child.shadowRoot);
          if (button) return button;
        }
      }
      return null;
    }

    update() {
      this.frame = 0;
      if (!this.isConnected) return;
      const modalSelectors = '#gyj-age-gate, #shopify-pc__banner, #shopify-pc__prefs__dialog, dialog[open], [aria-modal="true"], cart-drawer.active, header details[open], shopify-chat[open]';
      const blocked = [...document.querySelectorAll(modalSelectors)].some(element => this.visible(element));
      this.toggleAttribute('data-layer-blocked', blocked);
      this.removeAttribute('data-chat-blocked');
      this.style.setProperty('--gjl-nav-reserve-left', '0px');
      this.style.setProperty('--gjl-nav-reserve-right', '0px');
      this.removeAttribute('data-purchase-blocked');
      if (blocked || !matchMedia('(max-width: 749px)').matches) return;
      // Measure the fixed band independently so a stowed nav does not flicker.
      const bandHeight = parseFloat(getComputedStyle(this).height) || 60;
      const bandTop = innerHeight - bandHeight;
      const purchaseSelectors = 'main .product-form__submit, main button[name="add"], main form[action*="/cart/add"] button[type="submit"], main .shopify-payment-button, main shopify-accelerated-checkout, main .cart__checkout-button, main button[name="checkout"]';
      const purchaseBlocked = [...document.querySelectorAll(purchaseSelectors)].some(element => {
        if (!this.visible(element)) return false;
        const rect = element.getBoundingClientRect();
        return rect.bottom > bandTop && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
      });
      if (purchaseBlocked) {
        this.setAttribute('data-purchase-blocked', '');
        return;
      }
      const host = document.querySelector('shopify-chat:not([open]):not([data-gjl-chat-stowed])');
      const button = this.launcher(host?.shadowRoot);
      if (button !== this.chatButton) {
        if (this.chatButton) this.resize.unobserve(this.chatButton);
        this.chatButton = button;
        if (button) this.resize.observe(button);
      }
      if (!button) return;
      // Include the native nudge, when present, rather than only the icon.
      const container = button.closest('.activator-nudge') || button;
      const chat = container.getBoundingClientRect();
      const nav = this.getBoundingClientRect();
      if (!nav.height || chat.bottom <= nav.top || chat.top >= nav.bottom) return;
      const leftSide = chat.left + chat.width / 2 < innerWidth / 2;
      const reserve = Math.ceil(leftSide ? chat.right + 8 : innerWidth - chat.left + 8);
      const count = this.querySelectorAll('a').length;
      if (innerWidth - reserve < count * 44) {
        // A wide native nudge has priority. Restore automatically when it clears.
        this.setAttribute('data-chat-blocked', '');
        return;
      }
      this.style.setProperty(leftSide ? '--gjl-nav-reserve-left' : '--gjl-nav-reserve-right', `${reserve}px`);
    }
  });
}
